import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { isEmpty } from 'lodash-es';
import { useIsComponentStillOnItsPage } from '@namespace/cms';
import { useShallowMemo } from '@namespace/helpers';
import { useI18n } from '@namespace/i18n';
import { UserContext } from '@namespace/user';
import { MODE } from '../../constants';
import {
  getLineField,
  sportsbookActions,
  useLineField,
  useSportsbookStore
} from '../../store';
import { useFetchEventsFilter } from '../useFetchEventsFilter';
import filterByOnlyParsedNumbers from './filterByNumbers';

const { FETCH_EVENTS, FETCH_EVENTS_BY_LIMIT, SET_IS_LINE_CACHED } =
  sportsbookActions;

type UseFetchEventsProps = {
  lineKey: string;
  serviceId: number;
  filter: Record<string, unknown> | null;
  sportId: number;
  categoryIds: number[];
  tournamentsIds: number[];
  initialEventsLimit: number;
  moreEventsLimit: number;
};

export const useFetchEvents = ({
  lineKey,
  serviceId,
  filter = null,
  sportId,
  categoryIds: categoryIdsProp = [],
  tournamentsIds: tournamentsIdsProp = [],
  initialEventsLimit,
  moreEventsLimit
}: UseFetchEventsProps) => {
  const lineKeyRef = useRef<string | null>(lineKey);
  const [isEventsLoading, setIsEventsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [user] = useContext(UserContext);
  const { marginGroupId } = user;
  const { language: lang } = useI18n();
  // todo refactor: move 'empty' into selector OR (better) remove categories and tournaments dependency at all! (investigate)
  const { categories, tournaments } = useSportsbookStore([
    'categories',
    'tournaments'
  ]);
  const isCategoriesEmpty = isEmpty(categories);
  const isTournamentsEmpty = isEmpty(tournaments);
  const lineOffset = useLineField(lineKey, 'offset');

  const filterValue = useFetchEventsFilter(filter);
  const categoryIds = useShallowMemo(categoryIdsProp);
  const tournamentsIds = useShallowMemo(tournamentsIdsProp);
  const checkIsComponentStillOnItsPage = useIsComponentStillOnItsPage();

  useEffect(() => {
    lineKeyRef.current = lineKey;

    return () => {
      lineKeyRef.current = null;
    };
  }, [lineKey]);

  // region fetchEvents
  const fetchEvents = useCallback(async () => {
    try {
      setIsEventsLoading(true);

      await FETCH_EVENTS({
        lang,
        sportId,
        serviceId,
        // TODO: remove this typings after migration actions to ts
        categoryIds: categoryIds as never[],
        filterValue: filterValue!,
        marginGroupId,
        lineKey,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        tournamentsIds: filterByOnlyParsedNumbers(tournamentsIds),
        limit: initialEventsLimit,
        lineKeyRef
      });

      // The requester component has changed the current lineKey: this means that the previous request is no longer needed and can be safely discarded
      if (lineKeyRef.current !== lineKey) {
        return;
      }

      setIsEventsLoading(false);
    } catch (err) {
      console.error(err);
      setIsEventsLoading(false);
    }
  }, [
    categoryIds,
    filterValue,
    initialEventsLimit,
    lang,
    lineKey,
    marginGroupId,
    serviceId,
    sportId,
    tournamentsIds
  ]);

  useEffect(() => {
    // need to wait when categories and tournaments are loaded because only on
    // this stage we already have all data for correct requests without extra requests
    // to display relevant events for user without store overwriting
    if (
      sportId != null &&
      (serviceId === MODE.IN_PLAY ||
        (!isCategoriesEmpty && !isTournamentsEmpty))
    ) {
      // We navigated back to the line that was stashed, so we un-stash it without fetching
      if (getLineField(lineKey, 'isCached')) {
        SET_IS_LINE_CACHED(false, lineKey);
        setIsEventsLoading(false);
        return;
      }

      if (!checkIsComponentStillOnItsPage()) {
        return;
      }

      fetchEvents();
    }
  }, [
    checkIsComponentStillOnItsPage,
    fetchEvents,
    isCategoriesEmpty,
    isTournamentsEmpty,
    lineKey,
    serviceId,
    sportId
  ]);
  // endregion fetchEvents

  // region loadMoreEvents
  const loadMoreEvents = useCallback(async () => {
    try {
      setIsLoadingMore(true);
      await FETCH_EVENTS_BY_LIMIT({
        lang,
        sportId,
        serviceId,
        // TODO: remove this typings after migration actions to ts
        categoryIds: categoryIds as never[],
        filterValue: filterValue!,
        lineKey,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        tournamentsIds,
        offset: lineOffset,
        limit: moreEventsLimit,
        lineKeyRef
      });
      if (lineKeyRef.current !== lineKey) {
        return;
      }

      setIsLoadingMore(false);
    } catch {
      setIsLoadingMore(false);
    }
  }, [
    lang,
    sportId,
    serviceId,
    categoryIds,
    filterValue,
    lineKey,
    tournamentsIds,
    lineOffset,
    moreEventsLimit
  ]);
  // endregion loadMoreEvents

  return {
    isEventsLoading,
    isLoadingMore,
    loadMoreEvents
  };
};
